import { useCallback, useMemo } from "react";
import { Form, Formik } from "formik";
import * as yup from "yup";

import {
  Currency,
  type Currency as CurrencyType,
} from "@vega-api/generated/types/currency";
import { type DealTrackingLimitedPartnerBreakdown } from "@vega-api/generated/types/dealTrackingLimitedPartnerBreakdown";
import { MoneyField } from "@vega-core/components/Form/Fields/MoneyField";
import { ActionaryModal } from "@vega-core/components/Modals/ActionaryModal/ActionaryModal";
import { Notice } from "@vega-core/components/Notice/Notice";
import { type CoreCurrency, type CoreMoney } from "@vega-core/models/money";
import { m } from "@vega-core/paraglide/messages";

import { useIndicateInterest } from "../../../../CoInvestmentsLP/hooks/useIndicateInterest";

import { ModalButtonGroup } from "./ModalButtonGroup";

type EditIndicationOfInterestModalProps = {
  isOpen: boolean;
  onClose: () => void;
  item: DealTrackingLimitedPartnerBreakdown | null;
  dealCurrency?: CurrencyType;
  dealOpportunityId: string;
  dealId: string;
};

type FormValues = {
  amount: CoreMoney;
};

const validationSchema = yup.object({
  amount: yup.object({
    amount: yup.number(),
    currency: yup.string(),
  }),
});

const IndicationOfInterestFormContent = ({
  dealCurrency,
}: {
  dealCurrency: CoreCurrency;
}) => (
  <div className="space-y-4">
    <div>
      <MoneyField
        allowAbbreviations
        currency={dealCurrency as CoreCurrency}
        isRequired
        label={m.formFields_amount()}
        name="amount"
      />
    </div>
  </div>
);

export const EditIndicationOfInterestModal = ({
  isOpen,
  onClose,
  item,
  dealCurrency = Currency.USD,
  dealOpportunityId,
  dealId,
}: EditIndicationOfInterestModalProps) => {
  const { handleIndicateInterest, isSubmitting } = useIndicateInterest({
    dealOpportunityId,
    initialMoney: item?.indicativeAmount,
    dealId,
    isGpContext: true,
  });

  const initialValues = useMemo<FormValues>(
    () => ({
      amount: item?.indicativeAmount || {
        amount: 0,
        currency: dealCurrency,
      },
    }),
    [item, dealCurrency],
  );

  const handleSubmit = useCallback(
    (values: FormValues) => {
      if (!item) return;

      handleIndicateInterest(values.amount.amount, values.amount.currency);
      onClose();
    },
    [item, handleIndicateInterest, onClose],
  );

  const handleModalClose = useCallback(
    (open: boolean) => {
      if (!open && !isSubmitting) {
        onClose();
      }
    },
    [isSubmitting, onClose],
  );

  if (!item) return null;

  return (
    <ActionaryModal
      dataTestId="edit-indication-of-interest-modal"
      fluidHeight
      open={isOpen}
      setOpen={handleModalClose}
      title={m.coInvestments_tracking_indicativeOrder_modalTitle()}
    >
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        <Form className="space-y-6">
          <IndicationOfInterestFormContent dealCurrency={dealCurrency} />

          <Notice
            className="text-left"
            content={m.coInvestments_lpVisibilityWarning_description()}
            title={m.coInvestments_lpVisibilityWarning_title()}
            type="info"
            variant="accent"
          />

          <ModalButtonGroup
            cancelTestId="cancel-indication-of-interest-button"
            confirmTestId="confirm-indication-of-interest-button"
            isSubmitting={isSubmitting}
            onCancel={onClose}
          />
        </Form>
      </Formik>
    </ActionaryModal>
  );
};
